import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import { DateTime } from 'luxon'
import { ChevronLeft } from 'lucide-react'
import { toast } from 'sonner'
import { useHeaderExtra } from '@/contexts/header.context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Heading, Text } from '@/components/ui/typography'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { RotationShiftBoundary, Staff } from '@/lib/api/directory.client'
import { RoleMembersSection } from './RoleMembersSection'
import { RoleModeSection } from './RoleModeSection'
import { RotationSection } from './rotation/RotationSection'
import type { SchedulingMode, RoleDetailPageProps } from './role-detail-types'

export type {
  SchedulingMode,
  RoleSaveDraft,
  RoleDetailPageProps,
} from './role-detail-types'

export default function RoleDetailPage(props: RoleDetailPageProps) {
  const {
    isNew = false,
    roleName,
    roleDescription,
    allStaff,
    members,
    mode: initialMode,
    rotation,
    hasOfficeHours,
    todayIso = DateTime.now().toISODate() ?? '',
    onBack,
    onSave,
  } = props

  // ── Local form state — nothing persists until Save ───────────────────────
  const [name, setName] = useState(roleName)
  const [description, setDescription] = useState(roleDescription ?? '')
  const [mode, setMode] = useState<SchedulingMode>(initialMode)
  const [orderedIds, setOrderedIds] = useState<string[]>(
    members.map(member => member.id),
  )
  const [fallbackStaffId, setFallbackStaffId] = useState<string | null>(
    rotation.fallbackStaffId,
  )
  const [shiftBoundary, setShiftBoundary] = useState<RotationShiftBoundary>(
    rotation.shiftBoundary,
  )
  const [closedDayStart, setClosedDayStart] = useState<string | null>(
    rotation.closedDayStart,
  )
  // Session-edit set for the calendar (by hand or CSV apply).
  const [rotationEdits, setRotationEdits] = useState<
    Record<string, string | null>
  >({})
  const [unknownByDate, setUnknownByDate] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const initialMemberIds = useMemo(
    () => members.map(member => member.id).join(','),
    [members],
  )
  const dirty =
    name !== roleName ||
    (description.trim() || null) !== (roleDescription || null) ||
    mode !== initialMode ||
    orderedIds.join(',') !== initialMemberIds ||
    fallbackStaffId !== rotation.fallbackStaffId ||
    shiftBoundary !== rotation.shiftBoundary ||
    closedDayStart !== rotation.closedDayStart ||
    Object.keys(rotationEdits).length > 0

  // ── Unsaved-changes guard (in-app navigation + hard refresh/close) ───────
  const bypassGuard = useRef(false)
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string }
        nextLocation: { pathname: string }
      }) =>
        dirty &&
        !saving &&
        !bypassGuard.current &&
        currentLocation.pathname !== nextLocation.pathname,
      [dirty, saving],
    ),
  )
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const reset = () => {
    setName(roleName)
    setDescription(roleDescription ?? '')
    setMode(initialMode)
    setOrderedIds(members.map(member => member.id))
    setFallbackStaffId(rotation.fallbackStaffId)
    setShiftBoundary(rotation.shiftBoundary)
    setClosedDayStart(rotation.closedDayStart)
    setRotationEdits({})
    setUnknownByDate({})
  }

  const cancel = () => {
    if (isNew) {
      // Explicit discard — don't double-prompt via the blocker.
      bypassGuard.current = true
      onBack()
      return
    }
    if (!dirty) {
      // Nothing to revert — cancel just leaves the page.
      onBack()
      return
    }
    reset()
  }

  const save = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Give the role a name before saving')
      return
    }
    if (mode === 'ROTATION' && orderedIds.length === 0) {
      toast.error(
        'Add at least one member before enabling on-call rotation — there would be nobody to put on the calendar.',
      )
      return
    }
    setSaving(true)
    try {
      const fallbackChanged = fallbackStaffId !== rotation.fallbackStaffId
      await onSave({
        name: trimmedName,
        description: description.trim() || null,
        schedulingMode: mode,
        memberStaffIds: orderedIds,
        rotation: {
          fallbackStaffId: fallbackChanged ? fallbackStaffId : undefined,
          shiftBoundary:
            shiftBoundary !== rotation.shiftBoundary
              ? shiftBoundary
              : undefined,
          closedDayStart:
            closedDayStart !== rotation.closedDayStart
              ? closedDayStart
              : undefined,
          days: Object.entries(rotationEdits).map(([date, staffId]) => ({
            date,
            staffId,
          })),
        },
      })
      setRotationEdits({})
      setUnknownByDate({})
    } finally {
      setSaving(false)
    }
  }

  const memberStaff = useMemo(() => {
    const byId = new Map(allStaff.map(staff => [staff.id, staff]))
    return orderedIds
      .map(id => byId.get(id))
      .filter((staff): staff is Staff => !!staff)
  }, [allStaff, orderedIds])

  // Navbar title: the role's name (tracking live edits), "New role" while
  // creating, or a neutral placeholder if an existing name is cleared.
  const headerTitle = isNew
    ? name.trim() || 'New role'
    : name.trim() || 'Untitled role'
  useHeaderExtra(<span className="text-sm font-medium">{headerTitle}</span>, [
    headerTitle,
  ])

  return (
    <div className="max-w-4xl space-y-8 p-4 sm:p-8">
      <div className="space-y-4">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Roles
        </button>
        <div className="space-y-1.5">
          <Heading variant="subsection-title" as="h2">
            <label htmlFor="role-name">Name</label>
          </Heading>
          <Input
            id="role-name"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="e.g. Maintenance"
            className="max-w-md"
          />
        </div>
        <div className="space-y-1.5">
          <Heading variant="subsection-title" as="h2">
            <label htmlFor="role-description">Description</label>
          </Heading>
          <Input
            id="role-description"
            value={description}
            onChange={event => setDescription(event.target.value)}
            placeholder="What this role is for"
            className="max-w-md"
          />
        </div>
      </div>

      <RoleMembersSection
        allStaff={allStaff}
        orderedIds={orderedIds}
        onChange={setOrderedIds}
        rotationMode={mode === 'ROTATION'}
      />

      <RoleModeSection mode={mode} onChange={setMode} />

      {mode === 'ROTATION' && (
        <RotationSection
          members={memberStaff}
          baseAssignments={rotation.assignmentsByDate}
          edits={rotationEdits}
          unknownByDate={unknownByDate}
          fallbackStaffId={fallbackStaffId}
          shiftBoundary={shiftBoundary}
          closedDayStart={closedDayStart}
          hasOfficeHours={hasOfficeHours}
          todayIso={todayIso}
          minEditableDate={rotation.minEditableDate ?? todayIso}
          onAssign={(date, staffId) => {
            setRotationEdits(prev => ({ ...prev, [date]: staffId }))
            setUnknownByDate(prev => {
              if (!(date in prev)) return prev
              const next = { ...prev }
              delete next[date]
              return next
            })
          }}
          onFallbackChange={setFallbackStaffId}
          onShiftBoundaryChange={setShiftBoundary}
          onClosedDayStartChange={setClosedDayStart}
          onCsvApply={result => {
            setRotationEdits(prev => ({ ...prev, ...result.edits }))
            setUnknownByDate(prev => ({ ...prev, ...result.unknownByDate }))
          }}
        />
      )}

      {/* One explicit save for the whole page — details, members, mode, and
          calendar edits. Cancel discards (and backs out of a new role). */}
      <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
        {dirty && <Text variant="muted-xs">Unsaved changes</Text>}
        <Button variant="ghost" onClick={cancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={save} disabled={saving || (!dirty && !isNew)}>
          {saving ? 'Saving…' : isNew ? 'Create role' : 'Save'}
        </Button>
      </div>

      {blocker.state === 'blocked' && (
        <Dialog open onOpenChange={() => blocker.reset?.()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Discard unsaved changes?</DialogTitle>
              <DialogDescription>
                You've edited this role but haven't saved. If you leave now,
                your changes will be lost.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => blocker.reset?.()}>
                Stay
              </Button>
              <Button variant="destructive" onClick={() => blocker.proceed?.()}>
                Leave without saving
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
